import {
  Box,
  Button,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Trash2, } from "lucide-react";
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 2;

const HotelAdd_RoomInfo_3 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { saveStep, fetchDraft, hotelData, loading, } = useHotel();
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const [isSaving, setIsSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';

  const [promotionChoice, setPromotionChoice] = useState("");
  const [earlyBirdDiscount, setEarlyBirdDiscount] = useState("");
  const [earlyBirdDiscountDays, setEarlyBirdDiscountDays] = useState("");
  const [addonDiscount, setAddonDiscount] = useState("");
  const [customPromotionsDiscount, setCustomPromotionsDiscount] = useState("");
  const [customPromotionsLabel, setCustomPromotionsLabel] = useState("");
  const [customPromotionsArray, setCustomPromotionsArray] = useState([]);

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);


  useEffect(() => {
    if (hotelData.promotions) {
      const { promotionChoice, earlyBirdDiscount, earlyBirdDiscountDays, addonDiscount, customPromotions } = hotelData.promotions;
      setPromotionChoice(promotionChoice || "");
      setEarlyBirdDiscount(earlyBirdDiscount || "");
      setEarlyBirdDiscountDays(earlyBirdDiscountDays || "");
      setAddonDiscount(addonDiscount || "");
      if (customPromotions && customPromotions.length > 0) {
        setCustomPromotionsArray(customPromotions);
      }
    }
  }, [hotelData]);

  const addCustomPromotion = () => {
    setCustomPromotionsArray((prev) => [...prev, { discount: customPromotionsDiscount, label: customPromotionsLabel }]);
  };

  const deleteCustomPromotion = (index) => {
    setCustomPromotionsArray((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);

      // Build promotions payload based on promotionChoice
      const promotionsData = {
        promotionChoice: promotionChoice,
      };

      if (promotionChoice === "earlyBird") {
        promotionsData.earlyBirdDiscount = Number(earlyBirdDiscount);
        promotionsData.earlyBirdDiscountDays = Number(earlyBirdDiscountDays);
      } else if (promotionChoice === "addon") {
        promotionsData.addonDiscount = Number(addonDiscount);
      } else if (promotionChoice === "custom") {
        promotionsData.promotionChoice = "custom";
        promotionsData.customPromotions = customPromotionsArray;
      }

      // Save promotions step
      await saveStep("promotions", promotionsData);

      // Navigate to next step

      if (edit) {
        navigate("/hotel/add/overview/" + id);
      } else {
        navigate("/hotel/add/room-info-add-1/" + id);
      }
    } catch (err) {
      console.error("Error saving promotions:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsSaving(true);

      // Build promotions payload based on promotionChoice
      const promotionsData = {
        promotionChoice: promotionChoice,
      };

      if (promotionChoice === "earlyBird") {
        promotionsData.earlyBirdDiscount = Number(earlyBirdDiscount);
        promotionsData.earlyBirdDiscountDays = Number(earlyBirdDiscountDays);
      } else if (promotionChoice === "addon") {
        promotionsData.addonDiscount = Number(addonDiscount);
      } else if (promotionChoice === "custom") {
        promotionsData.promotionChoice = "custom";
        promotionsData.customPromotions = customPromotionsArray;
      }

      // Save promotions step
      await saveStep("promotions", promotionsData);

      // Navigate to next step

      navigate("/hotel/onboarding");
    } catch (err) {
      console.error("Error saving promotions:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const discountFieldSx = {
    width: isMobile ? "70px" : "90px",
    minWidth: isMobile ? "70px" : "90px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px",
    },
  };

  const customLabelFieldSx = {
    width: isMobile ? "150px" : "260px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px",
    },
  };

  const radioSx = {
    color: "#D1D1D1",
    "&.Mui-checked": { color: "#31BACD" },
  };

  const isNextDisabled =
    !promotionChoice ||
    (promotionChoice === "earlyBird" && !earlyBirdDiscount) ||
    (promotionChoice === "earlyBird" && !earlyBirdDiscountDays) ||
    (promotionChoice === "addon" && !addonDiscount) ||
    (promotionChoice === "custom" && customPromotionsArray.length === 0) ||
    (promotionChoice === "custom" &&
      !customPromotionsArray[0]?.discount) ||
    (promotionChoice === "custom" &&
      !customPromotionsArray[0]?.label);


  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
            {t('RoomInfoStep3Title')}
            <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
              {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
            </Box>
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
              <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                {t('PromotionQuestionLabel')} <span style={{ color: "red" }}> *</span>
              </Typography>

              <RadioGroup
                value={promotionChoice}
                onChange={(e) => setPromotionChoice(e.target.value)}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio sx={radioSx} />}
                  label={t('NoPromotionLabel')}
                  sx={{ color: "#000", fontSize: isMobile ? "14px" : "16px", py: 1 }}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
                  <FormControlLabel
                    value="earlyBird"
                    control={<Radio sx={radioSx} />}
                    label=""
                    sx={{ mr: 0 }}
                  />
                  <TextField
                    type="number"
                    value={earlyBirdDiscount}
                    onChange={(e) => setEarlyBirdDiscount(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                    {t('DiscountLabel')}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000" }}>
                    {t('EarlyBirdPromotionLabel1')}
                  </Typography>
                  <TextField
                    type="number"
                    value={earlyBirdDiscountDays}
                    onChange={(e) => setEarlyBirdDiscountDays(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000" }}>
                    {t('EarlyBirdPromotionLabel2')}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
                  <FormControlLabel
                    value="addon"
                    control={<Radio sx={radioSx} />}
                    label=""
                    sx={{ mr: 0 }}
                  />
                  <TextField
                    type="number"
                    value={addonDiscount}
                    onChange={(e) => setAddonDiscount(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                    {t('DiscountLabel')}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000" }}>
                    {t('AddonPromotionLabel')}
                  </Typography>
                </Box>

                {/* Last empty input row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
                  <FormControlLabel
                    value={"custom"}
                    control={<Radio sx={radioSx} />}
                    label=""
                    sx={{ mr: 0 }}
                  />
                  <TextField
                    type="number"
                    value={customPromotionsDiscount || ""}
                    onChange={(e) => setCustomPromotionsDiscount(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                    {t('DiscountLabel')}
                  </Typography>
                  <TextField
                    placeholder={t('CustomPromotionPlaceholder')}
                    value={customPromotionsLabel || ""}
                    onChange={(e) => setCustomPromotionsLabel(e.target.value)}
                    sx={customLabelFieldSx}
                  />
                  <Button
                    onClick={addCustomPromotion}
                    sx={{
                      color: "#31BACD",
                      textTransform: "none",
                      fontSize: isMobile ? "14px" : "16px",
                      minWidth: "auto",
                    }}
                  >
                    {t('AddPromotion')}
                  </Button>
                </Box>

                {/* Custom promotions as radio options */}
                {customPromotionsArray.map((row, index) => (
                  <FormControlLabel
                    key={index}
                    value={`custom-${index}`}
                    checked={true}
                    disabled={true}
                    control={<Radio sx={radioSx} />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1, px: 2 }}>
                        <TextField
                          type="number"
                          value={row.discount}
                          disabled
                          sx={discountFieldSx}
                        />
                        <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                          {t('DiscountLabel')}
                        </Typography>
                        <TextField
                          placeholder={t('CustomPromotionPlaceholder')}
                          value={row.label}
                          disabled
                          sx={customLabelFieldSx}
                        />
                        <Button
                          onClick={() => {
                            const fillIndex = customPromotionsArray.findIndex(
                              (p) => p.discount === row.discount && p.label === row.label
                            );
                            deleteCustomPromotion(fillIndex);
                          }}
                          sx={{
                            color: "#31BACD",
                            textTransform: "none",
                            fontSize: isMobile ? "14px" : "16px",
                            minWidth: "auto",
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </Box>
                    }
                    sx={{ mr: 0 }}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/room-info-2/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_RoomInfo_3;
